import { activeEvents } from './projection';
import { bmi, gaDays, weightGain } from './calculations';
import { formatInstant } from '@/lib/utils/date';
import type { ClinicalEvent } from './types';
import type { PracticeConfiguration } from './rules';
import { trajectoryRange, type PrenatalAlertId } from './clinicalReferences';
export interface PrenatalRuleContext {
  edd: string | null;
  onDate: string;
  timeZone: string;
  prePregnancyWeightGrams?: number | null;
  heightCm?: number | null;
  fetusCount?: number;
  active?: boolean;
}
export interface PrenatalFlag {
  ruleId: string;
  eventId: string;
  severity: 'info' | 'warn' | 'critical';
  message: string;
  configurationVersion: number;
}
export function evaluatePrenatalRules(
  events: ClinicalEvent[],
  config: PracticeConfiguration,
  context: PrenatalRuleContext,
): PrenatalFlag[] {
  const refs = config.clinical_references;
  if (!refs?.enabled || !config.clinically_reviewed || context.active === false) return [];
  const active = activeEvents(events)
    .sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at) || a.sequence - b.sequence)
    .filter((e) => formatInstant(e.occurred_at, 'yyyy-MM-dd', context.timeZone) <= context.onDate);
  if (!active.length) return [];
  const enabled = new Set(config.prenatal_alerts ?? []);
  const result: PrenatalFlag[] = [];
  const push = (id: PrenatalAlertId, event: ClinicalEvent, severity: PrenatalFlag['severity'], message: string) => {
    if (enabled.has(id))
      result.push({ ruleId: id, eventId: event.id, severity, message, configurationVersion: config.version });
  };
  const today = gaDays(context.onDate, context.edd);
  for (const event of active) {
    const p = event.payload,
      ga = gaDays(formatInstant(event.occurred_at, 'yyyy-MM-dd', context.timeZone), context.edd);
    if (event.event_type === 'VITALS.BP') {
      if (
        Number(p.systolic) >= refs.bp_reference.criticalSystolic ||
        Number(p.diastolic) >= refs.bp_reference.criticalDiastolic
      )
        push(
          'R-PN-02',
          event,
          'critical',
          'Blood pressure exceeds the reviewed critical threshold; assess per practice protocol.',
        );
      else if (
        Number(p.systolic) >= refs.bp_reference.warnSystolic ||
        Number(p.diastolic) >= refs.bp_reference.warnDiastolic
      )
        push('R-PN-01', event, 'warn', 'Blood pressure exceeds the reviewed warning threshold.');
    }
    if (event.event_type === 'LAB.URINE_DIP' && ['2+', '3+', '4+'].includes(String(p.protein)))
      push('R-PN-03', event, 'warn', 'Documented urine protein is 2+ or greater.');
    if (
      event.event_type === 'PRENATAL.FUNDAL_HEIGHT' &&
      typeof p.cm === 'number' &&
      ga !== null &&
      ga >= refs.fh_window.minDays &&
      ga <= refs.fh_window.maxDays &&
      Math.abs(p.cm - ga / 7) > refs.fh_window.toleranceCm
    )
      push('R-PN-04', event, 'warn', 'Fundal height differs from gestational age beyond the reviewed tolerance.');
    if (event.event_type === 'PRENATAL.FHT') {
      const low = typeof p.bpm === 'number' ? p.bpm : p.low_bpm,
        high = typeof p.bpm === 'number' ? p.bpm : p.high_bpm;
      if (
        (typeof low === 'number' && low < refs.fetal_heart_range.minBpm) ||
        (typeof high === 'number' && high > refs.fetal_heart_range.maxBpm)
      )
        push('R-PN-10', event, 'warn', 'Fetal heart rate is outside the reviewed range.');
    }
    if (
      event.event_type === 'VITALS.WEIGHT' &&
      ga !== null &&
      typeof p.grams === 'number' &&
      context.prePregnancyWeightGrams &&
      context.heightCm
    ) {
      const value = bmi(context.prePregnancyWeightGrams / 1000, context.heightCm, refs.bmi_limits);
      const range =
        value === null
          ? null
          : trajectoryRange(refs.weight_gain_trajectories ?? [], value, context.fetusCount ?? 0, ga);
      const gain = weightGain(p.grams, context.prePregnancyWeightGrams);
      if (range && gain !== null && (gain / 1000 < range[0] || gain / 1000 > range[1]))
        push(
          'R-PN-05',
          event,
          'info',
          'Weight gain is outside the reviewed gestational-age trajectory. Review in clinical context.',
        );
    }
  }
  const latest = active[active.length - 1];
  if (!context.edd) push('R-PN-06', latest, 'warn', 'EDD is not recorded after a documented prenatal encounter.');
  if (today !== null && today >= refs.postdates.warnDays)
    push(
      'R-PN-07',
      latest,
      today >= refs.postdates.criticalDays ? 'critical' : 'warn',
      'Gestational age has reached the reviewed postdates threshold.',
    );
  const profile = active.filter((e) => e.event_type === 'PRENATAL.CONTEXT').at(-1)?.payload ?? {};
  const decisions = new Map<string, string>();
  for (const event of active.filter((e) => e.event_type === 'CONSENT.DECISION'))
    decisions.set(String(event.payload.test).trim().toLowerCase(), String(event.payload.decision));
  const rhDeclined = ['rhig', 'rhogam', 'rh immune globulin', 'rh-immune globulin'].some(
    (k) => decisions.get(k) === 'DECLINED',
  );
  const rhGiven =
    profile.rhig_documented === true ||
    active.some((e) => e.event_type === 'PRENATAL.VISIT_DETAILS' && e.payload.rhig_given === true);
  if (today !== null && today >= 202 && profile.rh_status === 'negative' && !rhGiven && !rhDeclined)
    push(
      'R-PN-08',
      latest,
      'warn',
      'Rh-negative status is documented; RhIG administration is not documented by 28w6d.',
    );
  const gbsKnown = ['positive', 'negative', 'declined'].includes(String(profile.gbs_status));
  if (today !== null && today >= 259 && !gbsKnown)
    push(
      'R-PN-09',
      latest,
      'warn',
      'GBS status is not recorded in the structured prenatal context at 37 weeks; review pregnancy laboratory results.',
    );
  return result;
}
